import { ReactNode, useCallback, useState } from "react";
import { Link, useLocation } from "wouter";
import { Home, FileText, Users, MoreHorizontal, Plus, Loader2 } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { useI18n } from "@/lib/i18n";
import Landing from "@/pages/Landing";
import { cn } from "@/lib/utils";
import { CaptureSheet } from "@/components/capture/CaptureSheet";
import { CaptureProvider } from "@/contexts/CaptureContext";

/**
 * Bottom tab shell (mobile-first): Home · Drafts · [Capture FAB] · Team · More.
 * This component owns the page frame and renders the SINGLE fixed bottom tab bar
 * (there is exactly one `<nav>` in the launch path). The page content scrolls in
 * a wrapper whose bottom padding always clears the bar + the FAB + the safe-area
 * inset, so no content can hide behind the nav. Capture is a center FAB that
 * opens a sheet (NOT a route).
 */

const LEFT = [
  { href: "/", label: "Home", labelAr: "الرئيسية", icon: Home },
  { href: "/drafts", label: "Drafts", labelAr: "المسودات", icon: FileText },
];
const RIGHT = [
  { href: "/team", label: "Team", labelAr: "الفريق", icon: Users },
  { href: "/more", label: "More", labelAr: "المزيد", icon: MoreHorizontal },
];

function TabButton({
  href,
  label,
  icon: Icon,
  active,
}: {
  href: string;
  label: string;
  icon: typeof Home;
  active: boolean;
}) {
  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[10.5px] font-medium transition-colors",
        active ? "text-coral" : "text-muted-foreground hover:text-foreground",
      )}
    >
      <span
        className={cn(
          "grid h-8 w-12 place-items-center rounded-full transition-colors",
          active ? "bg-coral-soft/70" : "bg-transparent",
        )}
      >
        <Icon className="h-[18px] w-[18px]" strokeWidth={active ? 2.5 : 2} />
      </span>
      <span>{label}</span>
    </Link>
  );
}

export function TabShell({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const [loc] = useLocation();
  const [captureOpen, setCaptureOpen] = useState(false);
  const [captureDay, setCaptureDay] = useState<string | undefined>(undefined);
  const openCapture = useCallback((day?: string) => {
    setCaptureDay(day);
    setCaptureOpen(true);
  }, []);
  const isActive = (href: string) =>
    href === "/" ? loc === "/" : loc.startsWith(href);

  // AUTH GATE for the whole tabbed shell. An unauthenticated visitor (e.g. a
  // private-browsing tab) must see the sign-in landing — never the app shell
  // with placeholder data and permanently-disabled queries. While the session
  // check is in flight, show a neutral splash (not the landing) so a signed-in
  // reload doesn't flash the login screen.
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="grid min-h-[100dvh] place-items-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-coral" aria-label={isAr ? "جارٍ التحميل" : "Loading"} />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Landing />;
  }

  return (
    <CaptureProvider value={{ openCapture }}>
    {/* print:* — the app shell's warm background + full-viewport min-height and
        the bottom-nav padding are SCREEN chrome. They must be neutralized for
        print, otherwise they paint a beige block / reserve empty space beneath
        the dedicated white print document (which is the only printed artifact). */}
    <div className="relative min-h-[100dvh] bg-background print:min-h-0 print:bg-transparent">
      {/* Scrollable content. Bottom padding = bar height (~4.25rem) + FAB
          overhang + safe-area inset, so the last card always has breathing room
          above the nav and nothing is ever covered. Removed for print. */}
      <div className="pb-[calc(5.75rem+env(safe-area-inset-bottom))] print:pb-0">
        {children}
      </div>

      {/* The ONE bottom tab bar — fixed to the viewport bottom, safe-area aware. */}
      <nav
        aria-label={isAr ? "التنقل الرئيسي" : "Primary"}
        className="print:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border/70 bg-background/85 backdrop-blur-xl pb-[env(safe-area-inset-bottom)] shadow-[0_-10px_30px_-18px_rgba(40,30,20,0.25)]"
      >
        <div className="relative mx-auto flex max-w-lg items-stretch px-2">
          {LEFT.map(t => (
            <TabButton key={t.href} {...t} label={isAr ? t.labelAr : t.label} active={isActive(t.href)} />
          ))}

          {/* Center column: holds the "Capture" label aligned with the other
              tab labels; the FAB (absolute, below) sits over the empty icon row. */}
          <div className="flex w-16 shrink-0 flex-col items-center justify-center gap-1 py-2.5 text-[10.5px] font-medium text-coral">
            <span className="h-8 w-12" aria-hidden />
            <span>{isAr ? "التقاط" : "Capture"}</span>
          </div>

          {RIGHT.map(t => (
            <TabButton key={t.href} {...t} label={isAr ? t.labelAr : t.label} active={isActive(t.href)} />
          ))}

          {/* Center Capture FAB — anchored to the bar, lifted above it with a
              ring so it reads as part of the nav, never overlapping content. */}
          <button
            type="button"
            aria-label={isAr ? "التقاط" : "Capture"}
            onClick={() => openCapture()}
            className="absolute left-1/2 top-1 -translate-x-1/2 -translate-y-1/2 grid h-14 w-14 place-items-center rounded-full bg-coral text-white shadow-lg shadow-coral/30 ring-4 ring-background transition-transform active:scale-95"
          >
            <Plus className="h-6 w-6" strokeWidth={2.5} />
          </button>
        </div>
      </nav>

      {/* Capture sheet — center-FAB modal (not a route). Private signal pipeline. */}
      <CaptureSheet open={captureOpen} onOpenChange={setCaptureOpen} initialDay={captureDay} />
    </div>
    </CaptureProvider>
  );
}
